import type { BaanDetail } from "./useBanen";

/** The route segment that shows every jurylijst of one waarnemer on one page. */
export const ALLES = "alles";

/** On lijst `alles`, `#functie-16` scrolls to that functie. */
export const FUNCTIE_HASH = "functie-";

const ALGEMEEN_HOSTS = ["baancoördinator", "teamleider"];

export interface FunctieRij {
  /** The jurylijst's route segment: `algemeen`, the functie's position id, or `alles` for every functie of one waarnemer. */
  lijst: string;
  /** With `alles`: the functie to scroll to; undefined on the row that opens them all. */
  functie?: string;
  name: string;
  vragen: number;
  /** Set on the row that opens every functie of one waarnemer: how many there are. */
  functies?: number;
  /** Algemeen and the functie that judges it, as one row: opens both jurylijsten. */
  samen?: boolean;
  /** The assigned waarnemer this person fills in for; undefined for their own jurylijst. */
  namens?: { contactId: number; name: string };
}

/**
 * Every jurylijst this person can fill: their own, and for a beheerder also those of the assigned
 * waarnemers, namens them (ADR-0019). A waarnemer with more than one functie gets a first row that
 * opens them all on one page. `unassigned` counts the functies without a waarnemer.
 */
export function jouwFuncties(baan: BaanDetail, contactId: number | undefined, manage: boolean, algemeen: string) {
  // Algemeen is judged by whoever holds Baancoördinator, or else Teamleider; with neither on the baan it stands loose.
  const host = ALGEMEEN_HOSTS.map((name) => baan.functies.find((f) => f.name.toLowerCase() === name)).find(Boolean);
  const algemeenVragen = Number(baan.algemeenVraagCount);
  const parts = [
    ...(algemeenVragen > 0 && !host ? [{ positionId: null as number | null, name: algemeen, vragen: algemeenVragen, samen: false }] : []),
    ...baan.functies.map((f) => {
      const samen = algemeenVragen > 0 && f === host;
      return {
        positionId: Number(f.positionId),
        name: samen ? new Intl.ListFormat("nl").format([algemeen, f.name]) : f.name,
        vragen: Number(f.vraagCount) + (samen ? algemeenVragen : 0),
        samen,
      };
    }),
  ];
  const positionOf = (w: BaanDetail["waarnemers"][number]) => (w.positionId === null ? null : Number(w.positionId));
  const own = (w: BaanDetail["waarnemers"][number]) => Number(w.contactId) === contactId;

  const rows = parts.flatMap((part) =>
    baan.waarnemers
      .filter((w) => positionOf(w) === part.positionId && (own(w) || manage))
      .sort((a, b) => Number(own(b)) - Number(own(a)))
      .map(
        (w): FunctieRij => ({
          lijst: part.positionId === null || part.samen ? "algemeen" : String(part.positionId),
          ...(part.samen && { samen: true }),
          name: part.name,
          vragen: part.vragen,
          namens: own(w) ? undefined : { contactId: Number(w.contactId), name: w.name },
        }),
      ),
  );
  const unassigned = manage ? parts.filter((part) => !baan.waarnemers.some((w) => positionOf(w) === part.positionId)).length : 0;

  return { rows: perWaarnemer(rows), unassigned };
}

/** Groups the rows per waarnemer in order of appearance; a waarnemer with several functies gets an `alles` row first. */
function perWaarnemer(rows: FunctieRij[]) {
  const groups = new Map<number | undefined, FunctieRij[]>();
  for (const row of rows) groups.set(row.namens?.contactId, [...(groups.get(row.namens?.contactId) ?? []), row]);
  return [...groups.values()].flatMap((group) =>
    group.length === 1
      ? group.map((row) => (row.samen ? { ...row, lijst: ALLES } : row))
      : [
          {
            lijst: ALLES,
            name: group.map((row) => row.name).join(", "),
            vragen: group.reduce((sum, row) => sum + row.vragen, 0),
            functies: group.length,
            namens: group[0].namens,
          },
          ...group.map((row) => ({ ...row, lijst: ALLES, functie: row.lijst })),
        ],
  );
}
